// ===========================================
// Add Product To Invoice (single invoice)
// ===========================================
// The invoice page's "Add product" button. Deliberately NOT the Products page's
// AddProductToInvoiceDialog (components/admin/pricing/), which is an audience
// broadcast: it resolves a set of families, offers PER_FAMILY/PER_STUDENT and a due
// date for the invoices it may create.
//
// Here the invoice — and therefore the family — is already fixed, so none of that
// applies. The admin picks a product and types a quantity: 2 folders for two mid-term
// joiners, 1 metronome for the family. (Andrew, 2026-09-24.)

import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Stack,
  Typography,
  Alert,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { useProducts } from '../../hooks/useProducts';
import { useAddProductToInvoice } from '../../hooks/useInvoices';
import { Product, formatProductPrice } from '../../api/product.api';

/** Mirrors addProductToInvoiceSchema's `quantity` bound on the server. */
const MAX_QUANTITY = 100;

interface AddProductToInvoiceItemDialogProps {
  open: boolean;
  onClose: () => void;
  invoiceId: string;
}

export default function AddProductToInvoiceItemDialog({
  open,
  onClose,
  invoiceId,
}: AddProductToInvoiceItemDialogProps) {
  const [product, setProduct] = useState<Product | null>(null);
  // Held as a STRING so the field can be emptied while retyping. Coercing to a
  // number on every keystroke floors an empty field to 1, which then makes the next
  // digit append ("" → 1 → typing 2 gives 12) — the admin cannot simply clear and
  // retype. Validity is derived below instead.
  const [quantity, setQuantity] = useState('1');
  const [unitPrice, setUnitPrice] = useState('');

  const { data: products, isLoading, isError } = useProducts({ isActive: true });
  const addMutation = useAddProductToInvoice();

  // Reset everything when the dialog reopens, so a previous add never leaks in.
  // The Dialog stays mounted across close/open, so without resetting the mutation a
  // failed add's red Alert is still on screen the next time it opens.
  useEffect(() => {
    if (open) {
      setProduct(null);
      setQuantity('1');
      setUnitPrice('');
      addMutation.reset();
    }
    // addMutation is a stable object from react-query; depending on it would re-run
    // this on every mutation state change and wipe the form mid-use.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Selecting a product seeds its catalogue price; the admin may still override it.
  useEffect(() => {
    setUnitPrice(product ? String(Number(product.price)) : '');
  }, [product]);

  const parsedQuantity = parseInt(quantity, 10);
  // Mirrors the server's zod bound (int, 1..100). `inputProps.max` is advisory — it
  // constrains the spinner, not typing — so without this a typed 999 submits into an
  // avoidable 400, and a typed 2.7 would be SILENTLY floored to 2 by parseInt while
  // the field still reads 2.7. Reject both here instead. (Measured 2026-09-24.)
  const quantityIsValid =
    /^\d+$/.test(quantity.trim()) && parsedQuantity >= 1 && parsedQuantity <= MAX_QUANTITY;
  const parsedPrice = parseFloat(unitPrice);
  const priceIsValid = Number.isFinite(parsedPrice) && parsedPrice >= 0;
  const lineTotal = priceIsValid && quantityIsValid ? parsedPrice * parsedQuantity : 0;

  // An inventory-tracked product cannot oversell — the server rejects it, so say so
  // here rather than letting the admin submit into a 400.
  const shortStock = useMemo(() => {
    if (!product?.trackInventory || !quantityIsValid) return false;
    return (product.quantity ?? 0) < parsedQuantity;
  }, [product, parsedQuantity, quantityIsValid]);

  const canSubmit = !!product && quantityIsValid && priceIsValid && !shortStock;

  const handleSubmit = () => {
    if (!product || !canSubmit) return;
    addMutation.mutate(
      {
        id: invoiceId,
        data: {
          productId: product.id,
          quantity: parsedQuantity,
          // Only send a price that actually differs from the catalogue — otherwise
          // let the server resolve it, so the two cannot drift apart.
          ...(parsedPrice !== Number(product.price) ? { unitPrice: parsedPrice } : {}),
        },
      },
      { onSuccess: () => onClose() }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Product</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {isError && <Alert severity="error">Could not load products. Please try again.</Alert>}

          <Autocomplete
            options={products ?? []}
            loading={isLoading}
            value={product}
            onChange={(_, next) => setProduct(next)}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Product"
                placeholder="Search products"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatProductPrice(option.price)}
                    {option.trackInventory && ` · ${option.quantity ?? 0} in stock`}
                  </Typography>
                </Box>
              </Box>
            )}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label="Quantity"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              inputProps={{ min: 1, max: MAX_QUANTITY }}
              error={quantity.trim() !== '' && !quantityIsValid}
              helperText={
                quantity.trim() !== '' && !quantityIsValid
                  ? `Whole number, 1-${MAX_QUANTITY}`
                  : ' '
              }
              sx={{ width: 160 }}
            />
            <TextField
              label="Unit Price"
              type="number"
              value={unitPrice}
              onChange={(e) => setUnitPrice(e.target.value)}
              disabled={!product}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              inputProps={{ min: 0, step: 0.01 }}
              sx={{ flex: 1 }}
            />
          </Stack>

          {shortStock && (
            <Alert severity="warning">
              Only {product?.quantity ?? 0} in stock — reduce the quantity to continue.
            </Alert>
          )}

          {product && priceIsValid && quantityIsValid && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                {parsedQuantity} × {formatProductPrice(parsedPrice)}
              </Typography>
              <Typography variant="subtitle2">{formatProductPrice(lineTotal)}</Typography>
            </Box>
          )}

          {addMutation.isError && (
            <Alert severity="error">
              {addMutation.error instanceof Error
                ? addMutation.error.message
                : 'Failed to add the product.'}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={addMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit || addMutation.isPending}
        >
          {addMutation.isPending ? 'Adding…' : 'Add to Invoice'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
